import { type Ref, ref, watch } from 'vue'

import {
  normalizeCreatorDetail,
  normalizeCreatorExtraInfo,
} from '@/lib/adapters/creator-detail.adapter'
import { config, notification } from '@/lib/helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { externalApiRequest, request } from '@/lib/services'
import type {
  CreatorDetail,
  CreatorExtraInfo,
  CreatorUpdatePayload,
} from '@/lib/types/creator-detail.types'
import { useErrorStore } from '@/stores/error'

export const useGetCreatorDetail = (idRef: Ref<number>) => {
  const isLoading = ref(false)
  const isFetching = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<CreatorDetail | null>(null)
  const errorStore = useErrorStore()

  const getCreator = async (isReload = false) => {
    try {
      if (!isReload) {
        isFetching.value = true
      }
      isLoading.value = true
      error.value = null
      const id = idRef.value
      const response = await request.get(`/users/${id}`)
      data.value = normalizeCreatorDetail(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
      isFetching.value = false
    }
  }

  watch(idRef, () => void getCreator(), { immediate: true })

  return {
    data,
    getCreator,
    isLoading,
    isFetching,
    error,
  }
}

export const useGetCreatorExtraInfo = (idRef: Ref<number>) => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const data = ref<CreatorExtraInfo | null>(null)
  const errorStore = useErrorStore()

  const getExtraInfo = async () => {
    try {
      isLoading.value = true
      error.value = null
      const id = idRef.value
      const response = await request.get(`/users/${id}/extra-info`)
      data.value = normalizeCreatorExtraInfo(response.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watch(idRef, () => void getExtraInfo(), { immediate: true })

  return {
    data,
    getExtraInfo,
    isLoading,
    error,
  }
}

export const useUpdateCreator = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const updateCreator = async (id: number, payload: CreatorUpdatePayload): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.put(`/users/${id}`, payload)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    updateCreator,
    isLoading,
    error,
  }
}

export const useSaveCreatorTags = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const saveTags = async (userId: number, tags: string[]): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.post(`/admin/user-tags/${userId}`, { tags })
      notification.success('Tags updated')
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return {
    saveTags,
    isLoading,
    error,
  }
}

export const useZezamLogin = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const needsCreation = ref(false)
  const errorStore = useErrorStore()

  const fetchZezamUserId = async (userId: number): Promise<string | null> => {
    try {
      const response = await request.get(`/users/${userId}/identity`)
      const zezamUserId = response.data.zezam_user_id as string | undefined
      return zezamUserId ?? null
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === 404) {
        return null
      }
      throw e
    }
  }

  const redirectToZezam = (zezamUserId: string) => {
    window.location.replace(`${config.zezamRedirectUrl}?as-user-id=${zezamUserId}`)
  }

  const createZezamUser = async (userId: number): Promise<string> => {
    const response = await externalApiRequest.post(
      `${config.zezamApiUrl}/users/from-metapic`,
      { metapic_user_id: userId },
      { withCredentials: true },
    )

    const data = response.data as { id?: string; user_id?: string }
    const zezamUserId = data.id ?? data.user_id
    if (!zezamUserId) {
      throw new Error('Zezam user created but no user ID returned')
    }
    return String(zezamUserId)
  }

  const loginOrCreate = async (userId: number) => {
    try {
      isLoading.value = true
      error.value = null
      needsCreation.value = false

      const zezamUserId = await fetchZezamUserId(userId)

      if (zezamUserId) {
        redirectToZezam(zezamUserId)
        return
      }

      needsCreation.value = true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  const confirmCreation = async (userId: number) => {
    try {
      isLoading.value = true
      error.value = null
      needsCreation.value = false

      const zezamUserId = await createZezamUser(userId)
      redirectToZezam(zezamUserId)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  return {
    loginOrCreate,
    confirmCreation,
    needsCreation,
    isLoading,
    error,
  }
}
